import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CODE_LIST_UNAVAILABLE, type CodesState } from './useVocabulary'

/**
 * A picker that says what the choice IMPLIES.
 *
 * ## The complaint this answers
 *
 * "If I choose a category I don't even know what it implies." That was true of
 * every consequential field on the old create dialog: seven codes in a
 * `<select>`, no prose, and the difference between `TRACKER` and `BASELINE`
 * discoverable only by picking one and finding out later that the trend screen
 * has nothing to line it up with.
 *
 * So the options are cards, and each card carries one sentence about what
 * follows from picking it. The sentences live in `vocab.tsx` beside the labels.
 *
 * ## The options still come from the server
 *
 * `codes` is `useCodes(...)` — the served list — and the label and implication
 * are looked up FROM it. A code with no prose renders with its raw code and no
 * sentence rather than being dropped, and a code with prose but no server entry
 * cannot appear at all. That asymmetry is deliberate: this component can never
 * become the second source of truth for what may be chosen, which is the
 * PollSphere defect the whole vocabulary mechanism exists to prevent.
 */
export function ChoiceCards({
  state, value, onChange, labelOf, implicationOf, columns = 3, disabled,
}: {
  state: CodesState
  value: string
  onChange: (code: string) => void
  labelOf: (code: string) => string
  implicationOf: (code: string) => string
  columns?: 2 | 3
  disabled?: boolean
}) {
  if (state.loading) {
    return <p className="text-erp-xs text-muted-foreground py-2">Loading the options…</p>
  }
  if (state.unavailable) {
    // NOT an empty grid. An empty picker and a picker whose list failed to load
    // look identical, and only one of them means "there are none".
    return <p className="text-erp-xs text-red-700 py-2">{CODE_LIST_UNAVAILABLE}</p>
  }
  return (
    <div className={cn('grid gap-2', columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
      {state.codes.map((code) => {
        const active = code === value
        const implies = implicationOf(code)
        return (
          <button
            key={code}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(code)}
            className={cn(
              'text-left border rounded-md p-2.5 transition-colors disabled:opacity-50',
              active
                ? 'border-chocolate bg-chocolate-soft/30'
                : 'border-border hover:bg-muted/40',
            )}
          >
            <span className="flex items-center gap-1.5">
              {active && <Check className="h-3.5 w-3.5 text-chocolate shrink-0" />}
              <span className="text-erp-sm font-medium">{labelOf(code)}</span>
            </span>
            {implies && (
              <span className="block text-erp-xs text-muted-foreground mt-1">{implies}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The same thing as a compact `<select>`, for the fields whose implication is
 * worth one line under the control rather than a grid of cards.
 *
 * The prose is still shown — under the select, for the CHOSEN value. A form
 * where only some of the consequential fields explain themselves is a form
 * where a reader learns not to expect an explanation.
 */
export function ChoiceSelect({
  state, value, onChange, labelOf, implicationOf, disabled,
}: {
  state: CodesState
  value: string
  onChange: (code: string) => void
  labelOf: (code: string) => string
  implicationOf?: (code: string) => string
  disabled?: boolean
}) {
  const implies = implicationOf?.(value) ?? ''
  return (
    <div className="space-y-1">
      <select
        className="erp-input h-9 w-full"
        value={value}
        disabled={disabled || state.loading || state.unavailable}
        onChange={(e) => onChange(e.target.value)}
      >
        {state.loading && <option value="">Loading…</option>}
        {state.codes.map((code) => (
          <option key={code} value={code}>{labelOf(code)}</option>
        ))}
      </select>
      {state.unavailable
        ? <p className="text-erp-xs text-red-700">{CODE_LIST_UNAVAILABLE}</p>
        : implies ? <p className="text-erp-xs text-muted-foreground">{implies}</p> : null}
    </div>
  )
}

/** A labelled block inside a step. `Field` wraps its child in a `<label>`, which
 * is wrong for a group of buttons — a label may contain one control, not nine. */
export function StepField({
  label, hint, required, children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-erp-xs font-medium text-muted-foreground">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </p>
      {children}
      {hint && <p className="text-erp-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}
